export const RiskLevelValues = {
  CRITICAL: "critical",
  HIGH: "high",
  MODERATE: "moderate",
  LOW: "low",
} as const;

export const StatusValues = {
  APPROVED: "approved",
  CONDITIONAL: "conditional",
  PENDING: "pending",
  REJECTED: "rejected",
  REASSESSMENT: "reassessment",
} as const;

export const TypeValues = {
  GENERATIVE_AI: "GENERATIVE_AI",
  VISION_AI: "VISION_AI",
  AGENT_AI: "AGENT_AI",
  PREDICTIVE_AI: "PREDICTIVE_AI",
  CONVERSATIONAL_AI: "CONVERSATIONAL_AI",
  DECISION_AI: "DECISION_AI",
} as const;

export type RiskLevel = (typeof RiskLevelValues)[keyof typeof RiskLevelValues];
export type Status = (typeof StatusValues)[keyof typeof StatusValues];
export type AIType = (typeof TypeValues)[keyof typeof TypeValues];

// ============================================
// INVENTORY LIST RESPONSE
// Maps to: InventoryListResponse.java
// ============================================

export interface InventoryListResponse {
  items: InventoryItem[];
  // Pagination metadata fields
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface InventoryItem {
  id: string;
  tenant_id: number;
  name: string;
  vendor: string | null;
  description: string | null;
  type: string[] | null; // Changed from string to array
  logo_url: string | null;
  website_url: string | null;
  status: string | null;
  status_label: string | null;
  risk_level: string | null;
  risk_level_label: string | null;
  compliance_tags: string[] | null;
  approval: AiApprovalInfo | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  source: string | null; // Detection source: "zscaler", "code_insights", "manual", "user_requested" (scalar, kept for backward compat)
  sources: string[] | null; // Multiple detection sources (new array field from backend)
  usage: AiUsageMetrics | null;
  catalog: AiCatalogInfo | null;
}

/**
 * Usage metrics for AI inventory items.
 * Maps to: AiUsageMetrics.java (java-utils common package)
 */
export interface AiUsageMetrics {
  user_count: number | null;
  departments: string[] | null;
  user_emails: string[] | null;
  metrics_updated_at: string | null;
}

/**
 * Approval information for AI inventory items.
 * Maps to: AiApprovalInfo.java (java-utils common package)
 */
export interface AiApprovalInfo {
  scope: string | null;
  departments: string[] | null;
  approved_by: string | null;
  approved_at: string | null;
  expires_at: string | null;
  conditions: string | null;
  submitted_by: string | null;
  submitted_at: string | null;
}

/**
 * AI Catalog information - shared data structure.
 * Maps to: AiCatalogInfo.java (java-utils)
 */
export interface AiCatalogInfo {
  domain: string | null;
  name: string | null;
  vendor: string | null;
  type: string | null;
  logo_url: string | null;
  risk_level: string | null;
  risk_classification_reason: string | null;
  is_ai_native: boolean | null;
  ai_native_explanation: string | null;
  description_short: string | null;
  links: AiCatalogLinks | null;
  company: AiCatalogCompany | null;
  compliance: AiCatalogCompliance | null;
  popularity: AiCatalogPopularity | null;
  other_details: AiCatalogOtherDetails | null;
  tags: AiCatalogTags | null;
  sources: string[] | null;
  catalog_updated_at: string | null;
}

// ============================================
// INVENTORY DETAIL RESPONSE
// Note: Backend now uses InventoryItem for both list and detail endpoints
// This type alias is kept for backward compatibility
// ============================================

export type InventoryDetailResponse = InventoryItem;

// ============================================
// INVENTORY FILTERS RESPONSE
// Maps to: InventoryFiltersResponse.java
// ============================================

export interface InventoryFiltersResponse {
  types: FilterOption[];
  risk_levels: FilterOption[];
  statuses: FilterOption[];
  departments: FilterOption[];
  detection_sources: FilterOption[];
  sort_options: SortOption[];
}

export interface FilterOption {
  value: string;
  label: string;
  count: number | null;
}

export interface SortOption {
  value: string;
  label: string;
  default_order: string;
}

// ============================================
// INVENTORY LIST REQUEST
// Maps to: InventoryListRequest.java (query params)
// ============================================

export interface InventoryListRequest {
  name?: string[];
  type?: string[];
  riskLevel?: string[];
  status?: string[];
  department?: string[];
  source?: string[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

// ============================================
// INVENTORY CREATE REQUEST
// Maps to: InventoryCreateRequest.java
// ============================================

export interface InventoryCreateRequest {
  catalog_name?: string;
  catalog_domain?: string;
  custom_app?: CustomApp;
  type?: string[];
  status: string;
  risk_level?: string;
  compliance_tags?: string[];
  approval?: ApprovalInfo;
  notes?: string;
}

export interface CustomApp {
  name: string;
  vendor?: string;
  type?: string;
  description?: string;
  website_url?: string;
}

export interface ApprovalInfo {
  scope?: string;
  departments?: string[];
  approved_by?: string;
  approved_at?: string;
  expires_at?: string;
  conditions?: string;
}

// ============================================
// INVENTORY UPDATE REQUEST
// Maps to: InventoryUpdateRequest.java
// ============================================

export interface InventoryUpdateRequest {
  status?: string;
  risk_level?: string;
  compliance_tags?: string[];
  approval?: ApprovalInfo;
  notes?: string;
}

// ============================================
// CATALOG FILTERS RESPONSE
// Maps to: CatalogFiltersResponse.java (java-utils)
// ============================================

export interface CatalogFiltersResponse {
  types: CatalogFilter[];
  risk_levels: CatalogFilter[];
  sort_options: CatalogFilter[];
  verification_options: CatalogFilter[];
}

export interface CatalogFilter {
  value: string | number | boolean;
  label: string;
  count?: number;
  icon?: string;
  color?: string;
  bg_color?: string;
  description?: string;
  default_order?: string;
}

// ============================================
// CATALOG ITEM (for catalog list endpoint)
// Maps to: AiCatalogItem.java (java-utils)
// ============================================

export interface AiCatalogItem {
  // New top-level fields from backend API update
  domain?: string; // Primary key (e.g., "chatgpt.com")
  type?: string[]; // Array of AI types (e.g., ["GENERATIVE_AI", "DOCUMENT_AI"])
  risk_level?: string; // Risk level classification
  risk_classification_reason?: string | null; // Reason for risk classification
  // Existing fields
  name: string;
  is_ai_native?: boolean;
  ai_native_explanation?: string;
  description_short?: string;
  links?: AiCatalogLinks;
  company?: AiCatalogCompany;
  compliance?: AiCatalogCompliance;
  branding?: AiCatalogBranding;
  popularity?: AiCatalogPopularity;
  other_details?: AiCatalogOtherDetails;
  tags?: AiCatalogTags;
  sources?: string[];
}

// ============================================
// CATALOG PAGE RESPONSE (for paginated catalog)
// Maps to: AiCatalogPageResponse.java (java-utils)
// ============================================

export interface AiCatalogPageResponse {
  items: AiCatalogItem[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

// ============================================
// CATALOG REQUEST PARAMS
// Query parameters for getCatalog API
// ============================================

export interface GetCatalogParams {
  page?: number;
  pageSize?: number;
  sortBy?:
    | "domain"
    | "app_name"
    | "vendor"
    | "risk_level"
    | "created_at"
    | "updated_at";
  sortDir?: "asc" | "desc";
  // Filter params
  type?: string[];
  vendor?: string;
  risk_level?: string[];
  name?: string[];
  tag?: string;
}

export interface AiCatalogLinks {
  website?: string;
  documentation?: string;
  privacy_policy?: string;
  terms_of_use?: string;
}

export interface AiCatalogCompany {
  name?: string;
  founded?: string;
  founders?: string[];
  headquarters?: string;
  funding?: {
    total_raised?: string;
    last_round?: string;
    last_round_date?: string;
    investors?: string[];
  };
}

export interface AiCatalogCompliance {
  certifications?: string[];
  compliance_page_link?: string;
}

export interface AiCatalogBranding {
  icon_url?: string;
  brand_assets_page?: string;
}

export interface AiCatalogPopularity {
  monthly_active_users?: string;
  app_store_rating?: number;
  total_downloads?: string;
}

export interface AiCatalogOtherDetails {
  launched_date?: string;
  pricing_model?: string;
  key_features?: string[];
  integrations?: string[];
  api_available?: boolean;
}

export interface AiCatalogTags {
  risk_tier?: string[];
  ai_type?: string[];
  deployment_model?: string[];
  data_handling?: string[];
  data_types_supported?: string[];
  compliance_certifications?: string[];
  use_case?: string[];
  security_features?: string[];
  ai_model_info?: string[];
  output_controls?: string[];
  pricing_model?: string[];
  vendor_profile?: string[];
  integration?: string[];
}

export const TimelineAggregateByValues = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
} as const;

export type TimelineAggregateBy =
  (typeof TimelineAggregateByValues)[keyof typeof TimelineAggregateByValues];

export const TimelineAggFieldValues = {
  RISK_LEVEL: "risk_level",
  STATUS: "status",
  TYPE: "type",
  SOURCE: "source",
  DEPARTMENT: "department",
} as const;

export type TimelineAggField =
  (typeof TimelineAggFieldValues)[keyof typeof TimelineAggFieldValues];

export const TimelineMetricValues = {
  INVENTORY_COUNT: "inventory_count",
  USER_COUNT: "user_count",
  USAGE_COUNT: "usage_count",
} as const;

export type TimelineMetric =
  (typeof TimelineMetricValues)[keyof typeof TimelineMetricValues];

export interface InventoryTimelineRequest {
  aggregateBy: TimelineAggregateBy;
  aggField?: TimelineAggField;
  metric?: TimelineMetric;
  startDate?: string;
  endDate?: string;
  filters?: Record<string, string[]>;
}

export interface TimelineDataPoint {
  period: string;
  group: string | null;
  count: number;
}

export interface InventoryTimelineResponse {
  data: TimelineDataPoint[];
  aggregateBy: TimelineAggregateBy;
  aggField: TimelineAggField | null;
  totalCount: number;
}

// ============================================
// INVENTORY STATS API (for Donut/HeatMap/Bubble widgets)
// Maps to: InventoryStatsRequest.java, InventoryStatsResponse.java
// ============================================

/**
 * Supported aggregation fields for inventory statistics
 */
export const InventoryAggFieldValues = {
  RISK_LEVEL: "risk_level",
  STATUS: "status",
  TYPE: "type",
  SOURCE: "source",
  DEPARTMENT: "department",
  APP_NAME: "app_name",
} as const;

export type InventoryAggField =
  (typeof InventoryAggFieldValues)[keyof typeof InventoryAggFieldValues];

/**
 * Supported metrics for inventory statistics
 * Uses same values as TimelineMetricValues
 */
export const InventoryMetricValues = TimelineMetricValues;

export type InventoryMetric = TimelineMetric;

/**
 * Request DTO for inventory statistics API
 * Endpoint: POST /api/v1/aiem/inventoryV2/stats
 */
export interface InventoryStatsRequest {
  /** Primary aggregation field: risk_level, status, type, source, department, app_name */
  aggField: InventoryAggField;
  /** Secondary aggregation field for nested stats (HeatMap/Bubble). When provided, creates nested stats. */
  secAggField?: InventoryAggField;
  /** Metric to compute: inventory_count (default), user_count, usage_count */
  metric?: InventoryMetric;
  /** Start date for filtering (ISO format: YYYY-MM-DD) */
  startDate?: string;
  /** End date for filtering (ISO format: YYYY-MM-DD) */
  endDate?: string;
  /** Filters to apply. Keys: risk_level, status, type, department, source, name */
  filters?: Record<string, string[]>;
  /** Include items with missing/null aggregation field values. Default: false */
  includeMissing?: boolean;
  /** Number of top items to return (default: 10, max: 20) - for Usage Donut widget */
  topN?: number;
}

/**
 * Single stat entry for inventory statistics response
 */
export interface InventoryStat {
  /** The aggregation field value (e.g., "critical", "approved") */
  name: string;
  /** Human-readable label (e.g., "Critical", "Approved") */
  label?: string;
  /** Count for this bucket */
  count: number;
  /** Nested stats for secondary aggregation (HeatMap/Bubble) */
  stats?: Record<string, InventoryStat>;
}

/**
 * Response DTO for inventory statistics API
 */
export interface InventoryStatsResponse {
  /** Stats grouped by primary aggregation field */
  stats: Record<string, InventoryStat>;
  /** Total count across all groups */
  totalCount: number;
  /** Count of items not included in top N (for Usage Donut) */
  otherCount?: number;
  /** Number of "other" items not in top N (for Usage Donut) */
  otherItemCount?: number;
}

// ============================================
// API WRAPPER RESPONSE
// Standard ArmorCode API response wrapper
// ============================================

export interface BaseResponse<T> {
  data: T;
  message?: string;
  status?: string;
  code?: number;
}

export interface AIInventoryWidgetFilters {
  type?: string[];
  riskLevel?: string[];
  status?: string[];
  name?: string[];
  department?: string[];
  source?: string[];
}

// ============================================
// LOG UPLOAD API
// Endpoints: POST /api/v1/aiem/log-upload/presign, /complete
// ============================================

export enum LogUploadSourceType {
  ZSCALER = "zscaler",
  ZSCALER_ZPA = "zscaler-zpa-s3",
  CROWDSTRIKE_EDR = "crowdstrike-edr",
  SENTINELONE = "sentinelone",
  GOOGLE_WORKSPACE = "google-workspace",
}

export interface LogUploadPresignRequest {
  sourceType: LogUploadSourceType;
  fileName: string;
}

export interface LogUploadPresignResponse {
  signedUrl: string;
  s3Key: string;
  bucket: string;
  expiresInSeconds: number;
}

export interface LogUploadCompleteRequest {
  sourceType: LogUploadSourceType;
  s3Key: string;
  bucket: string;
  fileName: string;
  fileSize: number;
  uploadStatus: "SUCCESS" | "FAILED";
}

export interface LogUploadCompleteResponse {
  status: "QUEUED" | "RECORDED";
  message: string;
}

// ============================================
// INVENTORY EXPORT
// Endpoint: POST /api/v1/aiem/inventoryV2/export
// ============================================

export type InventoryExportType = "summary" | "detailed";

export interface InventoryExportRequest {
  exportType: InventoryExportType;
  riskLevel?: string;
  status?: string;
  department?: string;
  search?: string;
  type?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface InventoryExportResponse {
  processingInBackground?: boolean;
  message?: string | null;
  presignedUrl?: string;
}
